import { Logger, EngineComponentFactory } from "neo-common";
import { ComponentTypes } from "../ComponentTypes";
import { StageController } from "../StageController";
import { ActionSet } from "../ActionSet";
import { AppContext } from "../../../Common/AppContext";
import { GameManager } from "../../Managers/GameManager";
import { ModuleManager } from "../../Managers/ModuleManager";

/**
 * The CanvasManager class is used for parsing elements which are extending display objects and render them to canvas.
 * @class CanvasManager
 */
export class CanvasManager {
    /**
     * Trace to browser console.
     * @method log
     * @param data any
     * @return void
     */
    private static log(data: any): void {
        if (DEBUG) {
            Logger.debugGroup("CanvasManager", data);
        }
    }

    /**
     * Parse display object elements from xml and add them to stage.
     * @method parseElements
     * @param {Element} startNode
     * @param {PIXI.Container} parent
     * @returns {Object}
     */
    static parseElements(startNode: Element, parent: PIXI.Container = null): Object {
        const children: Object = {};

        if (!startNode) {
            return children;
        }

        CanvasManager.getNodesList(startNode.firstElementChild)
            .reverse()
            .filter(
                (node: Element) =>
                    node.hasAttribute("Class") &&
                    EngineComponentFactory.isTypeOf(node.getAttribute("Class"), ComponentTypes.DisplayObject),
            )
            .forEach((node: Element) => {
                const element: any = EngineComponentFactory.get(node.getAttribute("Class"));
                element.create(node);

                if (node.hasAttribute("LinkId")) {
                    ActionSet.ElementsToLink[node.getAttribute("LinkId")] = element;
                }

                var noGamble: boolean = false;

                if (node.hasAttribute("Key")) {
                    if (node.getAttribute("Key") === "gamble" && !AppContext.gameDefinition.Gamble) {
                        noGamble = true;
                    }
                }

                if (!noGamble) {
                    CanvasManager._addElementToParent(element, parent);
                    CanvasManager._handleElementController(node, element);
                }

                children[node.nodeName] = element;
            });

        return children;
    }

    /**
     * Recursively extracts sibling nodes.
     * @method getNodesList
     * @param {Element} node
     * @param {Array<Element>} nodeList
     * @returns {Array<Element>}
     */
    private static getNodesList(node: Element, nodeList: Array<Element> = []): Array<Element> {
        if (!node) {
            return nodeList;
        }

        if (node.nextElementSibling) {
            nodeList = CanvasManager.getNodesList(node.nextElementSibling, nodeList);
        }

        return nodeList.concat(node);
    }

    /**
     * Adds element either to its parent or to the stage
     * @method _addElementToParent
     * @param {PIXI.DisplayObject} element
     * @param {PIXI.Container} parent
     * @returns void
     */
    private static _addElementToParent(element: any, parent: PIXI.Container): void {
        if (parent != null) {
            parent.addChild(element);
        } else if (element.children.length > 0) {
            StageController.addChild(element);
        }
    }

    /**
     * If node has controller attribute, (creates new controller, inits it and) registers it
     * @method _handleElementController
     * @param {Element} node
     * @param {any} element
     * @returns void
     */
    private static _handleElementController(node: Element, element: any): void {
        const controllerAttribute = node.hasAttribute("Controller") ? node.getAttribute("Controller") : "";
        if (controllerAttribute === "") {
            return;
        }

        const elementController: any = EngineComponentFactory.get(controllerAttribute);
        if (elementController != null) {
            elementController.startInitialization(element);
            if (node.hasAttribute("Key")) {
                if (!AppContext.gameDefinition.Gamble && node.getAttribute("Key") === "gamble") {
                    return;
                }
                ModuleManager.registerGameModule(node.getAttribute("Key"), elementController);
            }
        } else {
            if (DEBUG) {
                CanvasManager.log("ERROR no controller with name: " + controllerAttribute);
            }
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Managers/CanvasManager.ts
 **/